import React, { useState, useEffect } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";

export const Route = createFileRoute("/WinFormModern")({
  component: WinFormModern,
});

type Tab = "MONITOR" | "QUEUE" | "CONFIG";

function WinFormModern() {
  const navigate = useNavigate();
  
  // State for Modern UI
  const [activeTab, setActiveTab] = useState<Tab>("MONITOR");
  const [emailsSent, setEmailsSent] = useState(42801);
  const [isSyncing, setIsSyncing] = useState(false);
  const [logs, setLogs] = useState<string[]>([
    "[OK] SQL_CONNECT_SUCCESS",
    "[INFO] Engine scanning for new invoices..."
  ]);

  // State for Legacy UI
  const [winActiveTab, setWinActiveTab] = useState<Tab>("MONITOR");
  const [winLogs, setWinLogs] = useState<string>("Status: Idle...\nLast Sync: 10:42 AM\nErrors: 0");

  // Simulated live ERP polling
  useEffect(() => {
    const interval = setInterval(() => {
      setEmailsSent((prev) => prev + Math.floor(Math.random() * 2));
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  const triggerSync = () => {
    setIsSyncing(true);
    const newLog = `[SYSTEM] Manual sync triggered at ${new Date().toLocaleTimeString()}`;
    setLogs(prev => [newLog, ...prev].slice(0, 8));
    
    setTimeout(() => {
      setIsSyncing(false);
      setLogs(prev => ["[OK] Sync complete. 0 updates found.", ...prev].slice(0, 8));
    }, 2000);
  };

  const triggerLegacySync = () => {
    setWinLogs("Status: Busy...\nConnecting to SQL Server...");
    setTimeout(() => {
      setWinLogs("Status: Idle...\nLast Sync: " + new Date().toLocaleTimeString() + "\nErrors: 0");
    }, 1500);
  };

  const orders = [
    { id: "ORD-99281", customer: "Global Indust.", status: "PROCESSED", type: "INVOICE" },
    { id: "ORD-99282", customer: "Apex Tech", status: "QUEUED", type: "CONFIRM" },
    { id: "ORD-99283", customer: "Riverside Co.", status: "QUEUED", type: "INVOICE" },
    { id: "ORD-99284", customer: "North Star", status: "PENDING", type: "SHIPPING" },
    { id: "ORD-99285", customer: "Velocity Sys", status: "FAILED", type: "INVOICE" },
  ];

  return (
    <div style={containerStyle}>
      {/* Navigation */}
      <nav style={navStyle}>
        <div style={navBrand} onClick={() => navigate({ to: "/Portfolio" })} style={{ cursor: "pointer", display: "flex", alignItems: "center", gap: "12px" }}>
          <div style={avatarStyle}>JF</div>
          <span style={{ fontWeight: "bold", letterSpacing: "1px" }}>CASE_STUDY_01</span>
        </div>
        <div style={navLinks}>
          <button onClick={() => navigate({ to: "/Portfolio" })} style={backButtonStyle}>
            ← BACK_TO_PORTFOLIO
          </button>
        </div>
      </nav>

      <main style={mainContent}>
        {/* Header Section */}
        <header style={headerSection}>
          <div style={labelStyle}>MIGRATION_PROJECT</div>
          <h1 style={titleStyle}>ERP Order Emails Automation</h1>
          <p style={subtitleStyle}>Modernizing legacy SQL polling into a high-availability .NET microservice.</p>
          <div style={tagRow}>
            {["C#", ".NET 7", "SQL_SERVER", "AZURE_AD", "IIS_HOSTING", "WORKER_SERVICE"].map((t) => (
              <span key={t} style={tagStyle}>{t}</span>
            ))}
          </div>
        </header>

        {/* 01. THE JOURNEY: Discovery to Deployment */}
        <section style={sectionBox}>
          <span style={sectionLabel}>01. THE_PROCESS_WALKTHROUGH</span>
          <div style={processGrid}>
            <div style={processStep}>
              <div style={stepCircle}>01</div>
              <h4 style={stepTitle}>STAKEHOLDER DISCOVERY</h4>
              <p style={stepText}>
                Conducted 1-on-1 meetings with the **Customer Service Team**. Discovered they were manually checking the old WinForms app every hour because they didn't trust the "silent" failures. Their main request: **Real-time visibility.**
              </p>
            </div>
            <div style={processStep}>
              <div style={stepCircle}>02</div>
              <h4 style={stepTitle}>LEGACY INTEGRATION</h4>
              <p style={stepText}>
                Instead of a risky database rewrite, I reused the **existing SQL Stored Procedures** for data integrity while wrapping them in a modern Repository pattern to handle transient connection faults.
              </p>
            </div>
            <div style={processStep}>
              <div style={stepCircle}>03</div>
              <h4 style={stepTitle}>IDENTITY & AUTOMATION</h4>
              <p style={stepText}>
                Integrated **Azure AD Service Principals** for secure DB access. Task automation was moved from a local "Task Scheduler" to a robust **Background Worker Service** hosted on **IIS** with auto-recycle logic.
              </p>
            </div>
          </div>
        </section>

        {/* 02. INTERACTIVE COMPARISON */}
        <section style={sectionBox}>
          <span style={sectionLabel}>02. SYSTEM_EVOLUTION_SANDBOX</span>
          <div style={flexRow}>
            
            {/* LEGACY WINFORMS */}
            <div style={columnHalf}>
              <p style={labelStyle}>PRIOR_STATE: WINFORMS_APP</p>
              <div style={winWindow}>
                <div style={winTitleBar}>
                  <span>ERP_Mail_v2.1.exe</span>
                  <div style={{ display: 'flex', gap: '2px' }}>
                    <div style={winBtn}>_</div><div style={winBtn}>□</div><div style={{ ...winBtn, backgroundColor: '#c05050', color: '#fff' }}>×</div>
                  </div>
                </div>
                <div style={winTabRow}>
                  <div onClick={() => setWinActiveTab("MONITOR")} style={winActiveTab === "MONITOR" ? winTabActive : winTab}>Monitor</div>
                  <div onClick={() => setWinActiveTab("QUEUE")} style={winActiveTab === "QUEUE" ? winTabActive : winTab}>Queue</div>
                  <div onClick={() => setWinActiveTab("CONFIG")} style={winActiveTab === "CONFIG" ? winTabActive : winTab}>Settings</div>
                </div>
                <div style={winContent}>
                  <div style={winInner}>
                    {winActiveTab === "MONITOR" && (
                      <>
                        <label style={winTextLabel}>SQL Query Window (Min):</label>
                        <input type="text" defaultValue="30" style={winInput} />
                        <div style={winLogBox}><pre style={{ margin: 0, font: 'inherit' }}>{winLogs}</pre></div>
                        <button style={winButton} onClick={triggerLegacySync}>Force Sync</button>
                      </>
                    )}
                    {winActiveTab === "QUEUE" && (
                       <div style={{ fontSize: '10px', color: '#000', lineHeight: '1.4' }}>
                         <strong>Pending Buffer:</strong> 5 items<br/><br/>
                         [ORD-99281] - READY<br/>
                         [ORD-99282] - WAIT<br/>
                         [ORD-99283] - WAIT<br/>
                         [ORD-99284] - ERR_LOCKED
                       </div>
                    )}
                    {winActiveTab === "CONFIG" && (
                       <div style={{ fontSize: '10px', color: '#000', lineHeight: '1.4' }}>
                         <strong>SMTP_HOST:</strong> 192.168.1.42<br/>
                         <strong>DB_STRING:</strong> Server=Prod;UID=Admin;PWD=***
                       </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* MODERN WEB INTERFACE */}
            <div style={columnHalf}>
              <p style={{ ...labelStyle, color: '#3b82f6' }}>CURRENT_STATE: .NET_MICROSERVICE</p>
              <div style={webWindow}>
                <div style={webTabRow}>
                  {(['MONITOR', 'QUEUE', 'CONFIG'] as Tab[]).map(t => (
                    <span key={t} onClick={() => setActiveTab(t)}
                      style={{ ...webTab, color: activeTab === t ? '#3b82f6' : '#555', borderBottom: activeTab === t ? '2px solid #3b82f6' : 'none' }}>
                      {t}
                    </span>
                  ))}
                </div>

                <div style={{ minHeight: '240px' }}>
                  {activeTab === 'MONITOR' && (
                    <div>
                      <div style={webStatusHeader}>
                        <span style={{ color: '#444' }}>ENGINE_STATUS:</span>
                        <span style={{ color: '#10b981' }}>{isSyncing ? '● SYNCING' : '● ACTIVE_IDLE'}</span>
                      </div>
                      <div style={metricCard}>
                        <h2 style={metricValue}>{emailsSent.toLocaleString()}</h2>
                        <span style={metricLabel}>EMAILS_SENT_TOTAL</span>
                      </div>
                      <div style={webLogTerminal}>
                        {logs.map((log, i) => (
                          <div key={i} style={{ color: log.includes('OK') ? '#10b981' : log.includes('SYSTEM') ? '#f59e0b' : '#3b82f6', marginBottom: '2px' }}>{log}</div>
                        ))}
                      </div>
                    </div>
                  )}

                  {activeTab === 'QUEUE' && (
                    <div style={{ maxHeight: '240px', overflowY: 'auto' }}>
                      <table style={tableStyle}>
                        <thead>
                          <tr style={{ textAlign: 'left', color: '#444' }}>
                            <th style={thStyle}>REF_ID</th>
                            <th style={thStyle}>STATE</th>
                          </tr>
                        </thead>
                        <tbody>
                          {orders.map(o => (
                            <tr key={o.id} style={{ borderBottom: '1px solid #1a1a1a' }}>
                              <td style={tdStyle}>{o.id}</td>
                              <td style={{ ...tdStyle, color: o.status === 'PROCESSED' ? '#10b981' : o.status === 'FAILED' ? '#ef4444' : '#f59e0b' }}>{o.status}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}

                  {activeTab === 'CONFIG' && (
                    <div>
                      <label style={configLabel}>MANAGED_IDENTITY_STATUS</label>
                      <div style={{ marginTop: '5px', padding: '12px', border: '1px solid #222', borderRadius: '8px', backgroundColor: '#0a0a0a' }}>
                        <div style={{ fontSize: '10px', color: '#3b82f6', marginBottom: '5px' }}>AZURE_APP_REGISTRATION</div>
                        <div style={{ fontSize: '11px', color: '#aaa' }}>Reader-Contributor-ERP-Role</div>
                      </div>
                      <button style={webButton} onClick={triggerSync} disabled={isSyncing}>
                        {isSyncing ? 'POLLING_SQL...' : 'FORCE_PROCEDURE_EXEC'}
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* 03. IMPACT SUMMARY */}
        <section style={sectionBox}>
          <span style={sectionLabel}>03. ARCHITECTURAL_IMPACT</span>
          <div style={gridThree}>
            <div style={cardStyle}>
              <div style={cardNum}>01</div>
              <h4 style={cardTitle}>ZERO-TRUST AUTH</h4>
              <p style={cardText}>Removed hardcoded SQL credentials. The application now uses **Managed Identities** to acquire short-lived tokens, satisfying modern security audits.</p>
            </div>
            <div style={cardStyle}>
              <div style={cardNum}>02</div>
              <h4 style={cardTitle}>IIS AUTOMATION</h4>
              <p style={cardText}>The service is hosted as an **AlwaysRunning** IIS Web Application, utilizing Application Initialization to ensure 0% downtime during server reboots.</p>
            </div>
            <div style={cardStyle}>
              <div style={cardNum}>03</div>
              <h4 style={cardTitle}>UI REWRITE</h4>
              <p style={cardText}>Migrated from a local EXE to a **React/Tailwind** dashboard, allowing Customer Service to monitor order health from any company device.</p>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
}

/* STYLING CONSTANTS */

const containerStyle: React.CSSProperties = {
  backgroundColor: "#0a0a0a", color: "#e0e0e0", minHeight: "100vh",
  padding: "120px 24px 60px 24px", fontFamily: "monospace"
};

const navStyle: React.CSSProperties = {
  position: "fixed", top: 0, left: 0, right: 0, height: "70px",
  backgroundColor: "rgba(10, 10, 10, 0.8)", backdropFilter: "blur(12px)",
  borderBottom: "1px solid #222", display: "flex", justifyContent: "space-between",
  alignItems: "center", padding: "0 40px", zIndex: 1000
};

const navBrand: React.CSSProperties = { display: "flex", alignItems: "center", gap: "12px" };
const avatarStyle: React.CSSProperties = {
  width: "32px", height: "32px", backgroundColor: "#1a1a1a", border: "1px solid #333",
  display: "flex", alignItems: "center", justifyContent: "center", color: "#fff",
  fontSize: "12px", fontWeight: "bold", borderRadius: "8px"
};

const navLinks: React.CSSProperties = { display: "flex", gap: "30px" };
const backButtonStyle: React.CSSProperties = {
  backgroundColor: "transparent", border: "1px solid #333", color: "#666",
  padding: "8px 16px", fontSize: "11px", fontWeight: "bold", cursor: "pointer"
};

const mainContent: React.CSSProperties = { maxWidth: "1000px", margin: "0 auto" };
const headerSection: React.CSSProperties = { marginBottom: "60px", borderBottom: "1px solid #1a1a1a", paddingBottom: "40px" };
const titleStyle: React.CSSProperties = { fontSize: "32px", color: "#fff", margin: "10px 0", fontWeight: "bold" };
const subtitleStyle: React.CSSProperties = { fontSize: "16px", color: "#3b82f6", marginBottom: "25px", maxWidth: "600px" };

const tagRow: React.CSSProperties = { display: "flex", gap: "10px", flexWrap: "wrap" };
const tagStyle: React.CSSProperties = { fontSize: "10px", backgroundColor: "#1a1a1a", border: "1px solid #333", padding: "4px 12px", color: "#aaa", borderRadius: "6px" };

const sectionBox: React.CSSProperties = { marginBottom: "100px" };
const sectionLabel: React.CSSProperties = { fontSize: "10px", color: "#444", letterSpacing: "2px", display: "block", marginBottom: "30px" };
const flexRow: React.CSSProperties = { display: "flex", gap: "40px", flexWrap: "wrap" };
const labelStyle: React.CSSProperties = { fontSize: "10px", color: "#666", marginBottom: "12px", letterSpacing: "1px" };

/* Process Styles */
const processGrid: React.CSSProperties = { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))", gap: "30px" };
const processStep: React.CSSProperties = { padding: "20px", borderLeft: "1px solid #222" };
const stepCircle: React.CSSProperties = { width: "24px", height: "24px", borderRadius: "50%", border: "1px solid #3b82f6", color: "#3b82f6", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "10px", marginBottom: "15px" };
const stepTitle: React.CSSProperties = { fontSize: "12px", color: "#fff", marginBottom: "10px", fontWeight: "bold" };
const stepText: React.CSSProperties = { fontSize: "12px", color: "#777", lineHeight: "1.6" };

/* WinForms Styles */
const winWindow: React.CSSProperties = { backgroundColor: '#d4d0c8', border: '2px solid', borderColor: '#fff #404040 #404040 #fff', padding: '2px' };
const winTitleBar: React.CSSProperties = { backgroundColor: '#000080', color: '#fff', padding: '3px 5px', display: 'flex', justifyContent: 'space-between', fontSize: '11px', fontWeight: 'bold' };
const winBtn: React.CSSProperties = { width: '14px', height: '14px', backgroundColor: '#d4d0c8', border: '1px solid #404040', color: '#000', textAlign: 'center', lineHeight: '12px', fontSize: '9px' };
const winTabRow: React.CSSProperties = { display: 'flex', gap: '2px', padding: '4px 4px 0 4px' };
const winTab: React.CSSProperties = { padding: '4px 10px', backgroundColor: '#d4d0c8', border: '1px solid #808080', borderBottom: 'none', fontSize: '11px', color: '#000', cursor: 'pointer' };
const winTabActive: React.CSSProperties = { ...winTab, fontWeight: 'bold', marginTop: '-2px', paddingBottom: '7px', position: 'relative', zIndex: 2 };
const winContent: React.CSSProperties = { padding: '15px', height: '240px' };
const winInner: React.CSSProperties = { border: '2px inset #fff', padding: '15px', height: '100%', display: 'flex', flexDirection: 'column' };
const winTextLabel: React.CSSProperties = { color: '#000', fontSize: '11px', marginBottom: '5px' };
const winInput: React.CSSProperties = { width: '100%', border: '2px inset #fff', backgroundColor: '#fff', padding: '2px', fontSize: '12px', color: '#000' };
const winLogBox: React.CSSProperties = { flexGrow: 1, backgroundColor: '#fff', border: '1px solid #808080', marginTop: '10px', padding: '8px', fontSize: '11px', color: '#000', overflow: 'hidden' };
const winButton: React.CSSProperties = { marginTop: '10px', padding: '5px', backgroundColor: '#d4d0c8', border: '2px solid', borderColor: '#fff #404040 #404040 #fff', fontSize: '11px', color: '#000', cursor: 'pointer' };

/* Modern Web Styles */
const webWindow: React.CSSProperties = { backgroundColor: '#111', border: '1px solid #222', borderRadius: '16px', padding: '24px' };
const webTabRow: React.CSSProperties = { display: 'flex', gap: '20px', borderBottom: '1px solid #222', marginBottom: '20px' };
const webTab: React.CSSProperties = { fontSize: '10px', cursor: 'pointer', paddingBottom: '8px', fontWeight: 'bold' };
const webStatusHeader: React.CSSProperties = { display: 'flex', justifyContent: 'space-between', fontSize: '10px', marginBottom: '15px' };
const metricCard: React.CSSProperties = { backgroundColor: '#0a0a0a', border: '1px solid #222', padding: '20px', borderRadius: '12px', textAlign: 'center', marginBottom: '15px' };
const metricValue: React.CSSProperties = { fontSize: "32px", color: "#fff", margin: 0 };
const metricLabel: React.CSSProperties = { fontSize: "9px", color: "#444" };
const webLogTerminal: React.CSSProperties = { backgroundColor: '#050505', padding: '12px', borderRadius: '8px', fontSize: '10px', height: '100px', overflowY: 'auto' };
const configLabel: React.CSSProperties = { display: 'block', fontSize: '10px', color: '#444', marginBottom: '8px' };
const webInput: React.CSSProperties = { width: '100%', backgroundColor: '#0a0a0a', border: '1px solid #222', color: '#fff', padding: '10px', borderRadius: '8px', fontSize: '12px' };
const webButton: React.CSSProperties = { width: '100%', backgroundColor: '#3b82f6', color: '#fff', border: 'none', padding: '12px', borderRadius: '8px', fontWeight: 'bold', fontSize: '11px', cursor: 'pointer', marginTop: '10px' };

/* Detailed Breakdown Styles */
const gridThree: React.CSSProperties = { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: "20px" };
const cardStyle: React.CSSProperties = { backgroundColor: "#111", padding: "30px", borderRadius: "16px", border: "1px solid #222", position: "relative" };
const cardNum: React.CSSProperties = { fontSize: "40px", color: "#1a1a1a", fontWeight: "bold", position: "absolute", top: "10px", right: "20px" };
const cardTitle: React.CSSProperties = { fontSize: "14px", color: "#fff", marginBottom: "15px", fontWeight: "bold", position: "relative" };
const cardText: React.CSSProperties = { fontSize: "13px", color: "#888", lineHeight: "1.6", position: "relative" };
const columnHalf: React.CSSProperties = { flex: "1", minWidth: "350px" };
const tableStyle: React.CSSProperties = { width: '100%', borderCollapse: 'collapse', fontSize: '11px' };
const thStyle: React.CSSProperties = { paddingBottom: '10px', fontWeight: 'normal', borderBottom: '1px solid #222' };
const tdStyle: React.CSSProperties = { padding: '12px 0', color: '#fff' };

export default WinFormModern;